/*
  Vertical ticker for the announcement bar. Pairs with
  assets/announcement-ticker.css and the "ticker" layout in
  sections/announcement-bar.liquid.

  Rotation stops while the toggle is pressed, while the pointer is over the
  bar, while a link inside it has keyboard focus, and while the tab is in the
  background - a timer firing against a hidden tab only wastes work and lands
  the customer on a different message than the one they left.
*/
if (!customElements.get('announcement-ticker')) {
  customElements.define(
    'announcement-ticker',
    class AnnouncementTicker extends HTMLElement {
      connectedCallback() {
        this.slides = Array.prototype.slice.call(
          this.querySelectorAll('.announcement-ticker__slide')
        );
        if (this.slides.length < 2) return;

        this.interval = parseInt(this.dataset.autoplay, 10);
        if (!this.interval || isNaN(this.interval)) return;

        this.index = 0;
        this.paused = false;
        this.hovered = false;
        this.timer = null;

        this.toggle = this.querySelector('.announcement-ticker__toggle');
        if (this.toggle) {
          this.toggle.addEventListener('click', this.onToggle.bind(this));
        }

        this.addEventListener('mouseenter', this.onHover.bind(this, true));
        this.addEventListener('mouseleave', this.onHover.bind(this, false));
        this.addEventListener('focusin', this.onHover.bind(this, true));
        this.addEventListener('focusout', this.onHover.bind(this, false));

        this._onVisibility = this.onVisibility.bind(this);
        document.addEventListener('visibilitychange', this._onVisibility);

        this.bindPopupTriggers();

        this.sync();
        this.start();
      }

      disconnectedCallback() {
        this.stop();
        document.removeEventListener('visibilitychange', this._onVisibility);
      }

      /*
        Messages flagged "Opens the newsletter pop-up" reuse the same handle
        the floating newsletter tab and the PDP promo bar use: the pop-up is a
        modal-dialog carrying data-popup, and show() is called on it.

        The pop-up is a merchant setting and can be switched off, which would
        leave a message that looks clickable and does nothing. So a trigger
        that cannot find its modal drops out of the rotation entirely.
      */
      bindPopupTriggers() {
        var triggers = Array.prototype.slice.call(
          this.querySelectorAll('.announcement-ticker__popup')
        );
        if (!triggers.length) return;

        var _this = this;

        var resolve = function () {
          var modal = document.querySelector('modal-dialog[data-popup]');

          triggers.forEach(function (trigger) {
            var slide = trigger.closest('.announcement-ticker__slide');

            if (!modal || typeof modal.show !== 'function') {
              if (slide) {
                slide.remove();
                _this.slides = _this.slides.filter(function (s) {
                  return s !== slide;
                });
                if (_this.index >= _this.slides.length) _this.index = 0;
                _this.sync();
              }
              return;
            }

            trigger.addEventListener('click', function () {
              modal.show(trigger);
            });
          });
        };

        if (document.readyState === 'complete') {
          resolve();
        } else {
          window.addEventListener('load', resolve);
        }
      }

      start() {
        if (this.timer || this.paused || this.hovered || document.hidden) return;
        this.timer = setInterval(this.advance.bind(this), this.interval);
      }

      stop() {
        if (!this.timer) return;
        clearInterval(this.timer);
        this.timer = null;
      }

      onToggle() {
        this.paused = !this.paused;
        this.toggle.setAttribute('aria-pressed', String(this.paused));
        this.toggle.setAttribute(
          'aria-label',
          this.paused ? 'Play announcements' : 'Pause announcements'
        );
        this.paused ? this.stop() : this.start();
      }

      onHover(entering) {
        this.hovered = entering;
        entering ? this.stop() : this.start();
      }

      onVisibility() {
        document.hidden ? this.stop() : this.start();
      }

      advance() {
        var previous = this.slides[this.index];
        this.index = (this.index + 1) % this.slides.length;

        previous.classList.remove('is-active');
        previous.classList.add('is-leaving');

        // Let the outgoing slide finish travelling before it is reset to the
        // bottom, otherwise it visibly snaps back down through the bar.
        setTimeout(function () {
          previous.classList.remove('is-leaving');
        }, 500);

        this.sync();
      }

      sync() {
        var active = this.index;
        this.slides.forEach(function (slide, i) {
          var isActive = i === active;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', String(!isActive));
          slide.querySelectorAll('a').forEach(function (link) {
            link.tabIndex = isActive ? 0 : -1;
          });
        });
      }
    }
  );
}
